import {
	GPS,
	GpsSerialUploadTemplate,
	GpsUploadTemplate,
	RESS,
	RessSerialUploadTemplate,
	RessUploadTemplate,
} from "lib/util/constants";

export type FlexNetTemplateSector = typeof GPS | typeof RESS;

/**
 * FlexNet create-flow upload templates (blob names via Deployment).
 *
 * With Part Number: {@link GpsUploadTemplate} / {@link RessUploadTemplate} (Product Number | Serial).
 * Without Part Number: {@link GpsSerialUploadTemplate} / {@link RessSerialUploadTemplate} (Serial only).
 */
export function FlexNetUploadTemplateFileName(
	sectorType: FlexNetTemplateSector,
	partNumberRequired: boolean
): string {
	if (sectorType === RESS) {
		return partNumberRequired ? RessUploadTemplate : RessSerialUploadTemplate;
	}
	return partNumberRequired ? GpsUploadTemplate : GpsSerialUploadTemplate;
}

/**
 * Download button label.
 * Gate fails (PN required) → "Download template with Part for {sector}".
 * Gate passes → "Download template for {sector}".
 */
export function FlexNetUploadTemplateButtonLabel(
	sectorType: FlexNetTemplateSector,
	partNumberRequired: boolean
): string {
	return partNumberRequired
		? `Download template with Part for ${sectorType}`
		: `Download template for ${sectorType}`;
}

export function ResolveFlexNetTemplateSector(
	sectorName: string | undefined | null
): FlexNetTemplateSector {
	return sectorName?.toUpperCase() === RESS ? RESS : GPS;
}

export const AcceptedUploadFileType = "Accepted file type(s): .xlsx or .csv";

/** Shown when a FlexNet upload is missing Part Number + Serial Number columns. */
export const FlexNetUploadPartSerialColumnsRequired =
	`Required column(s): Part Number and Serial Number\n${AcceptedUploadFileType}`;

export const FlexNetUploadSerialOnlyColumnsRequired =
	`Required column(s): Serial Number\n${AcceptedUploadFileType}`;

/**
 * FlexNet PN-gate column layout check (frontend).
 * `null`/`undefined` skips the FlexNet check (gate not resolved yet, or
 * non-FlexNet). ASRS single-column checks are gated on `asrsUpload` separately.
 */
export function ValidateFlexNetUploadColumnCount(
	colCount: number,
	partNumberRequired: boolean | null | undefined
): string | null {
	if (partNumberRequired == null) {
		return null;
	}
	if (partNumberRequired && colCount < 2) {
		return FlexNetUploadPartSerialColumnsRequired;
	}
	if (!partNumberRequired && colCount !== 1) {
		return FlexNetUploadSerialOnlyColumnsRequired;
	}
	return null;
}
